import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  Renderer2,
  AfterViewChecked,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  ChatService,
  Message,
  AdminService,
  AssistantMessage,
  CreateFeedbackRequest,
} from 'ldt-chat-api';
import { NotificationService } from 'src/app/shared/notification-service/notification.service';
import { QuickBuild } from 'src/app/people/quick-build.service';
import { AuthService } from 'src/app/auth/service/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Log } from 'src/app/logger.service';
import { forkJoin, Observable, Subject, BehaviorSubject } from 'rxjs';
import { takeUntil, filter, switchMap } from 'rxjs/operators';
import { PeopleChatService, ChatThreadResponse } from 'src/app/core/services/people-chat.service';
import { MatDialog } from '@angular/material/dialog';
import { ThreadDetailsDialogComponent } from '../thread-details-dialog/thread-details-dialog.component';
import { HeapUtils } from 'src/app/api-client/heap.utils';
import { BrowserNotificationService } from '../../services/browser-notification.service';

@Component({
  selector: 'app-chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  standalone: false,
})
export class PeopleChatComponent implements AfterViewChecked, OnDestroy, OnChanges {
  @Input({ required: true }) threadId!: string;
  @ViewChild('chatScrollContainer') private chatScrollContainer: ElementRef;

  messages: Message[] = [];
  threadFeedback: boolean | undefined;
  showFeedbackInput: boolean | undefined;
  feedbackText: string = '';
  chatResponse: any;
  currentPrompt: string = '';
  isAdmin: Observable<boolean>;
  isLoading = false;
  orgId: string;
  feedbackPlaceholder = 'What was wrong with the answer?';
  private shouldScrollToBottom = false;
  expandedThinkingGroups = new Set<number>(); // Track which thinking message groups are expanded
  notifyWhenDone = false; // Track if user wants browser notification when chat completes
  notificationsSupported = false; // Track if browser supports notifications
  isSharedThread = false; // True when viewing a shared chat snapshot (read-only)
  isCancelling = false; // Track if a cancel request is in flight
  messageFeedback: Record<string, boolean | undefined> = {}; // Per-message feedback state (message_id -> isPositive)
  feedbackMessageId: string | null = null; // message_id for which the feedback textarea is shown
  editingMessageIndex: number | null = null; // Index of the user message being edited
  editingMessageText = ''; // Text content of the message being edited
  branchingMessageId: string | null = null; // message_id currently being branched
  private destroy$ = new Subject<void>(); // For cleanup on destroy
  private threadId$ = new BehaviorSubject<string | null>(null); // Track threadId changes to prevent race conditions
  private threadSubscription: Subscription | undefined; // Track active thread subscription for cleanup
  private cancelCurrentThreadLoad$ = new Subject<void>(); // Cancel in-flight thread loads

  constructor(
    private chatService: ChatService,
    private peopleChatService: PeopleChatService,
    private notify: NotificationService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private adminService: AdminService,
    private dialog: MatDialog,
    private browserNotificationService: BrowserNotificationService,
    private elRef: ElementRef,
    private renderer: Renderer2
  ) {}

  ngOnInit() {
    this.isAdmin = this.authService.$isAdmin;
    this.notificationsSupported = 'Notification' in window;

    let orgId = this.route.parent?.snapshot.paramMap.get('orgId');
    if (!orgId) {
      orgId = this.authService.getSelectedOrgIdValue;
    }
    // Allow empty orgId for pre-seeded shared threads — the thread is already
    // in the PeopleChatService cache so no API call with orgId will be made.
    this.orgId = orgId || '';
    this.authService.$getSelectedOrgId.pipe(takeUntil(this.destroy$)).subscribe((orgId) => {
      // Only react to genuine org changes. The selectedOrgId BehaviorSubject
      // re-emits the current value on subscribe and again whenever the org is
      // re-broadcast during navigation/guard resolution. Without this guard,
      // a duplicate emission of the same org would call resetChat() after the
      // thread already loaded, wiping this.messages and leaving the panel empty
      // (while the parent-owned artifacts panel stayed populated).
      if (orgId && orgId !== this.orgId) {
        this.orgId = orgId;
        this.resetChat();
      }
    });

    // Use switchMap to prevent race conditions when threadId changes rapidly
    this.threadId$
      .pipe(
        filter((id) => !!id),
        switchMap((id) => {
          // CRITICAL: Cancel any in-flight thread loading operations
          // This ensures previous thread subscriptions are cleaned up
          this.cancelCurrentThreadLoad$.next();

          // Unsubscribe from previous thread updates immediately
          if (this.threadSubscription) {
            this.threadSubscription.unsubscribe();
            this.threadSubscription = undefined;
          }

          // Clear state immediately when thread changes
          this.messages = [];
          this.chatResponse = null;
          this.isLoading = true;
          this.shouldScrollToBottom = false;
          this.isCancelling = false;
          this.messageFeedback = {};
          this.feedbackMessageId = null;
          this.editingMessageIndex = null;
          this.editingMessageText = '';
          this.branchingMessageId = null;

          // Load initial thread data (will cache it)
          return this.peopleChatService.getThread(this.orgId, id!);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (thread: ChatThreadResponse) => {
          // Initial load complete - now subscribe to updates
          // Double-check we're still on the same thread (in case of rapid switching)
          if (this.threadId$.value !== thread.id) {
            return;
          }
          this.subscribeToThreadUpdates(thread.id);
        },
        error: (err) => {
          Log.e('Error getting chat thread:', err);
          this.isLoading = false;
          if (err.status === 404) {
            this.notify.error('Chat thread not found');
          } else {
            this.notify.error('Error getting chat thread. Please try again later.');
            Log.s('Error getting chat thread:', 'error', {
              error: err,
              orgId: this.orgId,
              thread: this.threadId || '',
            });
          }
        },
      });

    // Initialize with current threadId
    if (this.threadId) {
      this.isSharedThread = this.threadId.startsWith('shared-');
      this.threadId$.next(this.threadId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Handle changes to threadId from parent component
    if (changes['threadId']) {
      const change = changes['threadId'];

      // Only reload if the value actually changed (not first change, handled in ngOnInit)
      if (!change.isFirstChange() && change.currentValue !== change.previousValue) {
        this.isSharedThread = change.currentValue?.startsWith('shared-') || false;
        // Update the BehaviorSubject - switchMap will cancel any in-flight requests
        this.threadId$.next(change.currentValue);
      }
    }
  }

  ngOnDestroy() {
    // Unsubscribe from thread updates
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
    }

    // Complete subjects
    this.cancelCurrentThreadLoad$.next();
    this.cancelCurrentThreadLoad$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewChecked() {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
    }
    this.wrapMarkdownTables();
  }

  // Wrap any newly-rendered markdown tables in a scroll container so wide
  // tables get a horizontal scrollbar instead of pushing the bubble wider.
  // ngx-markdown writes raw <table> elements; we wrap them post-render.
  // The data-md-wrapped attribute makes this idempotent across change-detection
  // cycles, and the `:not([data-md-wrapped])` selector means the inner work is
  // skipped on every cycle except the one immediately after a new table renders.
  private wrapMarkdownTables(): void {
    const tables: NodeListOf<HTMLTableElement> = this.elRef.nativeElement.querySelectorAll(
      'markdown.markdown-content table:not([data-md-wrapped])'
    );
    tables.forEach((table) => {
      this.renderer.setAttribute(table, 'data-md-wrapped', 'true');
      const parent = this.renderer.parentNode(table);
      if (!parent) return;
      const wrap = this.renderer.createElement('div');
      this.renderer.addClass(wrap, 'md-table-wrap');
      this.renderer.insertBefore(parent, wrap, table);
      this.renderer.appendChild(wrap, table);
    });
  }

  private scrollToBottom(): void {
    try {
      // Use setTimeout to ensure DOM is fully updated
      setTimeout(() => {
        if (this.chatScrollContainer) {
          this.chatScrollContainer.nativeElement.scrollTop =
            this.chatScrollContainer.nativeElement.scrollHeight;
        }
        this.shouldScrollToBottom = false;
      }, 0);
    } catch (err) {
      // Ignore scroll errors
      this.shouldScrollToBottom = false;
    }
  }

  sendMessage() {
    if (!this.currentPrompt?.trim()) return;

    const prompt = this.currentPrompt;

    // Track chat submission with flag for new vs continuation
    HeapUtils.track('chat_message_submitted', {
      chat_type: this.threadId ? 'continuation' : 'new',
      message_count: this.messages.length,
    });

    // Track Intercom event for chat submission
    if ((window as any).Intercom) {
      (window as any).Intercom('trackEvent', 'chat_request_submitted', {
        chat_type: this.threadId ? 'continuation' : 'new',
      });
    }

    this.isLoading = true;
    this.shouldScrollToBottom = true;
    this.currentPrompt = '';

    // Use service for create or continue (service handles polling automatically)
    const operation$ = this.threadId
      ? this.peopleChatService.continueThread(this.orgId, this.threadId, prompt)
      : this.peopleChatService.createThread(this.orgId, prompt);

    operation$
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.cancelCurrentThreadLoad$) // Cancel if user switches threads
      )
      .subscribe({
        next: (thread: ChatThreadResponse) => {
          // Update state from service response
          this.messages = thread.messages;
          this.chatResponse = thread;
          this.shouldScrollToBottom = true;

          // Check if done
          if (thread.status === 'idle') {
            this.isLoading = false;

            // Send browser notification if enabled and page is hidden
            if (this.notifyWhenDone && this.browserNotificationService.isPageHidden()) {
              this.browserNotificationService.sendChatCompletionNotification(
                this.messages[this.messages.length - 1].text.substring(0, 100) + '...',
                () => {
                  window.focus();
                }
              );
            }

            // Check for Intercom handoff
            this.handleIntercomHandoff(thread);
          } else if (thread.status === 'error') {
            this.isLoading = false;
            this.notify.error(
              'The AI is having trouble with this question. Please try starting a new thread.'
            );
          } else if (thread.status === 'interrupted') {
            this.isLoading = false;
          }
        },
        error: (err) => {
          this.isLoading = false;
          if (err.status === 402) {
            this.notify.error(
              "You have reached your temporary chat limit for the month. We'll be in touch shortly."
            );
            Log.s('Chat quota exceeded', 'error', {
              thread: this.threadId || 'unknown',
              orgId: this.orgId,
            });
            return;
          }
          Log.e('Error in chat:', err);
          this.notify.error('Error with chat. Please try again later.');
          Log.s('Error with chat:', 'error', {
            error: err,
            orgId: this.orgId,
            thread: this.threadId || '',
          });
        },
      });
  }

  /**
   * Subscribe to thread updates from service
   * Service handles polling when thread is busy
   */
  private subscribeToThreadUpdates(threadId: string) {
    // Should already be unsubscribed in switchMap, but double-check for safety
    if (this.threadSubscription) {
      this.threadSubscription.unsubscribe();
      this.threadSubscription = undefined;
    }

    this.threadSubscription = this.peopleChatService
      .threadById$(threadId)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.cancelCurrentThreadLoad$) // Cancel if new thread load starts
      )
      .subscribe({
        next: (data: ChatThreadResponse | null) => {
          if (!data) return;

          // CRITICAL: Only process updates for the currently active thread
          // This prevents race conditions where updates from a previous thread
          // cause the UI to flip away from the current thread
          if (data.id !== this.threadId$.value) {
            return;
          }

          // Always set messages and response, regardless of status
          this.messages = data.messages;
          this.chatResponse = data;
          this.shouldScrollToBottom = true;

          if (data.status === 'idle') {
            // Thread is complete
            this.isLoading = false;

            // Check for Intercom handoff
            this.handleIntercomHandoff(data);
          } else if (data.status === 'error' || data.status === 'interrupted') {
            // Thread failed or was interrupted
            this.isLoading = false;
          } else {
            // Thread is still processing
            this.isLoading = true;
          }
        },
      });
  }

  /**
   * Cancel the currently running query
   */
  cancelRun() {
    if (!this.threadId || this.isCancelling) return;
    const currentThreadId = this.threadId;
    this.isCancelling = true;

    this.peopleChatService
      .cancelRun(this.orgId, currentThreadId)
      .pipe(takeUntil(this.destroy$), takeUntil(this.cancelCurrentThreadLoad$))
      .subscribe({
        next: (response) => {
          if (this.threadId !== currentThreadId) return;
          this.isCancelling = false;
          if (response.status === 'cancelled') {
            this.isLoading = false;
          }
        },
        error: () => {
          if (this.threadId !== currentThreadId) return;
          this.isCancelling = false;
        },
      });
  }

  /**
   * Submit per-message thumbs up/down feedback
   */
  messageQuickFeedback(message: any, isPositive: boolean) {
    const messageId = message.message_id;
    if (!messageId || !this.threadId) return;

    this.messageFeedback[messageId] = isPositive;
    this.feedbackMessageId = messageId;
    this.feedbackPlaceholder = isPositive
      ? 'Any additional feedback is helpful!'
      : 'What was wrong with the answer?';
    this.feedbackText = '';

    setTimeout(() => {
      this.showFeedbackInput = true;
    }, 0);

    this.peopleChatService
      .submitMessageFeedback(this.orgId, this.threadId, messageId, isPositive)
      .pipe(takeUntil(this.destroy$), takeUntil(this.cancelCurrentThreadLoad$))
      .subscribe({
        error: () => {
          delete this.messageFeedback[messageId];
          this.feedbackMessageId = null;
          this.showFeedbackInput = false;
        },
      });
  }

  /**
   * Start editing a user message (for edit & re-run)
   */
  startEditingMessage(index: number) {
    const message = this.messages[index] as any;
    if (message.role !== 'user' || !message.message_id) return;
    this.editingMessageIndex = index;
    this.editingMessageText = message.text;
  }

  /**
   * Cancel editing a message
   */
  cancelEditing() {
    this.editingMessageIndex = null;
    this.editingMessageText = '';
  }

  /**
   * Submit the edited message (edit & re-run)
   */
  submitEdit() {
    if (this.editingMessageIndex === null || !this.editingMessageText.trim()) return;

    const message = this.messages[this.editingMessageIndex] as any;
    if (!message.message_id || !this.threadId) return;

    const prompt = this.editingMessageText.trim();
    const messageId = message.message_id;

    this.editingMessageIndex = null;
    this.editingMessageText = '';
    this.isLoading = true;
    this.shouldScrollToBottom = true;

    this.peopleChatService
      .editAndRerun(this.orgId, this.threadId, messageId, prompt)
      .pipe(takeUntil(this.destroy$), takeUntil(this.cancelCurrentThreadLoad$))
      .subscribe({
        next: (thread: ChatThreadResponse) => {
          this.messages = thread.messages;
          this.chatResponse = thread;
          this.shouldScrollToBottom = true;

          if (thread.status === 'idle') {
            this.isLoading = false;
          } else if (thread.status === 'error' || thread.status === 'interrupted') {
            this.isLoading = false;
          }
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Branch the conversation from a specific message into a new thread
   */
  branchFromMessage(message: any) {
    const messageId = message.message_id;
    if (!messageId || !this.threadId || this.branchingMessageId) return;

    const currentThreadId = this.threadId;
    this.branchingMessageId = messageId;

    this.peopleChatService
      .branchChat(this.orgId, this.threadId, messageId)
      .pipe(takeUntil(this.destroy$), takeUntil(this.cancelCurrentThreadLoad$))
      .subscribe({
        next: (thread: ChatThreadResponse) => {
          this.branchingMessageId = null;
          if (this.threadId !== currentThreadId) return;
          // Navigate to the new branched thread
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { threadId: thread.id },
            queryParamsHandling: 'merge',
          });
        },
        error: () => {
          this.branchingMessageId = null;
        },
      });
  }

  sendFollowUpOrSampleQuestion(question: string) {
    this.currentPrompt = question;
    this.sendMessage();
  }

  quickFeedback(isPositive: boolean) {
    this.feedbackPlaceholder = isPositive
      ? 'Any additional feedback is helpful!'
      : 'What was wrong with the answer?';

    // Force change detection
    setTimeout(() => {
      this.showFeedbackInput = true;
    }, 0);

    // Submit initial feedback immediately
    this.threadFeedback = isPositive;
    const req: CreateFeedbackRequest = {
      feedback: isPositive ? 1 : 0,
      comment: this.feedbackPlaceholder,
    };
    this.chatService
      .createFeedback(this.orgId, this.threadId || 'unknown', undefined, req)
      .subscribe({
        next: () => {
          // Nothing to do here
        },
        error: () => this.notify.error('Error submitting feedback'),
      });
  }

  submitFeedback() {
    // Force change detection
    setTimeout(() => {
      this.showFeedbackInput = false;
    }, 0);

    // Use per-message feedback API when we have a message_id with a valid rating
    if (this.feedbackMessageId && this.threadId) {
      const isPositive = this.messageFeedback[this.feedbackMessageId];
      if (isPositive === undefined) {
        this.feedbackMessageId = null;
        return;
      }
      this.peopleChatService
        .submitMessageFeedback(
          this.orgId,
          this.threadId,
          this.feedbackMessageId,
          isPositive,
          this.feedbackText
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.feedbackText = '';
            this.feedbackMessageId = null;
          },
          error: () => {
            this.feedbackMessageId = null;
          },
        });
      return;
    }

    // Fallback: thread-level feedback for messages without message_id
    const req: CreateFeedbackRequest = {
      feedback: this.threadFeedback ? 1 : 0,
      comment: this.feedbackText,
    };
    this.chatService
      .createFeedback(this.orgId, this.threadId || 'unknown', undefined, req)
      .subscribe({
        next: () => {
          this.feedbackText = '';
        },
        error: () => this.notify.error('Error submitting feedback'),
      });
  }

  async toggleNotifyWhenDone(checked: boolean) {
    if (checked) {
      const granted = await this.browserNotificationService.requestPermission();
      if (granted) {
        this.notifyWhenDone = true;
        this.notify.success('Browser notifications enabled for chat completion');
      } else {
        this.notifyWhenDone = false;
        this.notify.warning(
          'Please allow notifications in your browser settings to use this feature'
        );
      }
    }
  }

  resetChat() {
    this.messages = [];
    this.currentPrompt = '';
    this.isLoading = false;
    this.threadFeedback = undefined;
    this.showFeedbackInput = false;
    this.feedbackText = '';
    this.isCancelling = false;
    this.messageFeedback = {};
    this.feedbackMessageId = null;
    this.editingMessageIndex = null;
    this.editingMessageText = '';
    this.branchingMessageId = null;
  }

  /**
   * Check if an artifact is currently being generated (artifact_pending is true)
   */
  get isArtifactPending(): boolean {
    return !!(this.chatResponse as any)?.artifact_pending;
  }

  /**
   * Check if the thread was cancelled (interrupted with an empty last message,
   * as opposed to a clarification interrupt which has text)
   */
  get wasCancelled(): boolean {
    if (this.chatResponse?.status !== 'interrupted') return false;
    const lastMsg = this.messages[this.messages.length - 1] as any;
    return lastMsg?.role === 'assistant' && !lastMsg?.text?.trim();
  }

  /**
   * Check if the message at the given index is the last visible assistant message in the thread
   */
  isLastAssistantMessage(index: number): boolean {
    for (let i = index + 1; i < this.messages.length; i++) {
      const msg = this.messages[i] as any;
      if (msg.role === 'assistant' && this.shouldDisplayMessage(msg, i)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if AI is currently thinking (has thinking messages without a following assistant response)
   */
  isThinkingInProgress(): boolean {
    if (this.messages.length === 0) return false;
    if (this.chatResponse?.status === 'busy') return true;

    // Look at recent messages to see if we have thinking messages at the end
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const message = this.messages[i] as any;

      // If we hit an assistant message, thinking is complete
      if (message.role === 'assistant') {
        return false;
      }

      // If we hit a thinking message, AI is still thinking
      if (message.role === 'thinking') {
        return true;
      }

      // If we hit a user message, no thinking in progress
      if (message.role === 'user') {
        return false;
      }
    }

    return false;
  }

  /**
   * Get the current batch of thinking messages to display
   */
  getActiveThinkingMessages(): Message[] {
    if (!this.isThinkingInProgress()) {
      return [];
    }

    const thinkingMessages: Message[] = [];

    // Walk backwards from the end, collecting thinking messages
    for (let i = this.messages.length - 1; i >= 0; i--) {
      const message = this.messages[i] as any;

      // Stop at assistant or user message
      if (message.role === 'assistant' || message.role === 'user') {
        break;
      }

      if (message.role === 'thinking') {
        thinkingMessages.unshift(message);
      }
    }

    return thinkingMessages;
  }

  /**
   * Get thinking messages that precede an assistant message at the given index
   */
  getThinkingMessagesBeforeAssistant(assistantIndex: number): Message[] {
    const thinkingMessages: Message[] = [];

    // Walk backwards from the assistant message, collecting thinking messages
    for (let i = assistantIndex - 1; i >= 0; i--) {
      const message = this.messages[i] as any;

      // Stop at user message (beginning of this conversation turn)
      if (message.role === 'user') {
        break;
      }

      // Collect thinking messages
      if (message.role === 'thinking') {
        thinkingMessages.unshift(message);
      }

      // Stop at another assistant message (shouldn't happen, but just in case)
      if (message.role === 'assistant') {
        break;
      }
    }

    return thinkingMessages;
  }

  /**
   * Check if there are thinking messages before this assistant message
   */
  hasThinkingMessagesBeforeAssistant(index: number): boolean {
    return this.getThinkingMessagesBeforeAssistant(index).length > 0;
  }

  /**
   * Toggle expansion state for a thinking message group
   */
  toggleThinkingExpanded(index: number): void {
    if (this.expandedThinkingGroups.has(index)) {
      this.expandedThinkingGroups.delete(index);
    } else {
      this.expandedThinkingGroups.add(index);
    }
  }

  /**
   * Check if a thinking group is expanded
   */
  isThinkingExpanded(index: number): boolean {
    return this.expandedThinkingGroups.has(index);
  }

  /**
   * Check if a message should be displayed (filter out thinking messages and empty assistant messages)
   */
  shouldDisplayMessage(message: Message | any, index: number): boolean {
    // Always show user messages
    if (message.role === 'user') {
      return true;
    }

    // Hide empty assistant messages from cancelled runs (type "interrupt" with no text)
    if (message.role === 'assistant' && !message.text?.trim()) {
      return false;
    }

    // Show assistant messages with content
    if (message.role === 'assistant') {
      return true;
    }

    // Hide thinking messages - they're shown in the thinking bubble instead
    if (message.role === 'thinking') {
      return false;
    }

    return true;
  }

  /**
   * TrackBy function to help Angular identify messages and avoid re-rendering
   */
  trackByMessageIndex(index: number, message: Message): any {
    return index;
  }

  openThreadDetails(): void {
    if (!this.threadId) {
      this.notify.warning('No thread ID available');
      return;
    }

    // Fetch both admin thread details and regular thread data
    forkJoin({
      adminThread: this.adminService.adminGetThread(this.threadId),
      thread: this.peopleChatService.getThread(this.orgId, this.threadId),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.dialog.open(ThreadDetailsDialogComponent, {
            data: {
              adminThread: response.adminThread,
              thread: response.thread,
            },
            width: '80vw',
            maxWidth: '1200px',
            maxHeight: '90vh',
          });
        },
        error: (err) => {
          this.notify.error('Error loading thread details. Please try again later.');
          Log.s('Error getting thread details:', 'error', {
            error: err,
            orgId: this.orgId,
            thread: this.threadId || '',
          });
        },
      });
  }

  /**
   * Check if the chat response contains an Intercom handoff and handle it
   */
  private handleIntercomHandoff(response: any): void {
    // Check if the last assistant message has an intercom_handoff
    const handoff = response.intercom_handoff;
    if (handoff?.original_query) {
      // Add a placeholder message to the chat thread
      const placeholderMessage: Message = {
        role: 'assistant',
        text: 'This question appears product-related and not a question that can be answered by our data. For product-related questions, our support agent can help. Click the support agent window on the left to send this message to that agent.',
      } as Message;

      // Add the placeholder message if it's not already there
      const lastMessage = this.messages[this.messages.length - 1];
      if (!lastMessage || lastMessage.text !== placeholderMessage.text) {
        this.messages.push(placeholderMessage);
        this.shouldScrollToBottom = true;
      }

      // Automatically open Intercom after a short delay to let the UI render
      setTimeout(() => {
        this.openIntercomWithMessage(handoff.original_query);
      }, 500);
    }
  }

  /**
   * Open Intercom chat with a prefilled message
   */
  openIntercomWithMessage(message: string): void {
    if ((window as any).Intercom && message) {
      // Skip Fin for AI-directive handoffs; reset in bootstrap's onHide so subsequent
      // direct launcher clicks still get Fin.
      (window as any).Intercom('update', { skip_fin: true });
      (window as any).Intercom('showNewMessage', message);

      // Track the handoff event
      HeapUtils.track('chat_intercom_handoff', {
        threadId: this.threadId,
        orgId: this.orgId,
      });
    }
  }
}
